import { formatCount, formatCurrency, formatRate } from './results-format';
import { NON_AID_POPULATION_LABEL } from '../results-step.constants';

import type {
  ActualResults,
  AidModelResultRowDto,
  EstimatedResults,
  NonAidRepackageDto,
} from 'api/generated/schemas';
import type { NonModeledCells, NonModeledRow } from '../results-step.types';

const NON_AID_KEY = 'non-aid';
const NON_AID_RESULT_KEY = 'NonAid';
const UNKNOWN_KEY = 'UNKNOWN';
const UNKNOWN_LABEL = 'Unknown';

const toCells = (row: AidModelResultRowDto | undefined): NonModeledCells => ({
  enrollment: formatCount(row?.enrollment),
  netTuitionRevenue: formatCurrency(row?.netTuitionRevenue),
  institutionalAid: formatCurrency(row?.institutionalAid),
  tuitionDiscountRate: formatRate(row?.tuitionDiscountRate),
});

const buildRow = (
  key: string,
  resultKey: string,
  population: string,
  estimatedRow: AidModelResultRowDto | undefined,
  lastYearRow: AidModelResultRowDto | undefined,
  showLastYear: boolean,
  repackageStatus: NonAidRepackageDto | undefined
): NonModeledRow => {
  const cells = toCells(estimatedRow);

  return {
    key,
    resultKey,
    population,
    ...cells,
    lastYear: showLastYear ? toCells(lastYearRow) : undefined,
    enrollmentValue: estimatedRow?.enrollment ?? 0,
    tuitionDiscountRateValue: estimatedRow?.tuitionDiscountRate ?? 0,
    originalEnrollmentValue: estimatedRow?.originalEnrollment,
    originalTuitionDiscountRateValue: estimatedRow?.originalTuitionDiscountRate,
    repackageWithNewRegimes: !!repackageStatus,
    repackageStatus,
  };
};

const findByKey = (
  rows: AidModelResultRowDto[] | undefined,
  key: string | undefined
): AidModelResultRowDto | undefined =>
  key ? rows?.find((row) => row.key === key) : undefined;

/**
 * Builds Tab 2 rows from the estimated band. When `showLastYear` is on, each row
 * is paired with its matching actual-band counterpart for read-only comparison.
 */
export const buildNonModeledRows = (
  estimatedResults: EstimatedResults | undefined,
  actualResults: ActualResults | undefined,
  showLastYear: boolean
): NonModeledRow[] => {
  const rows: NonModeledRow[] = [];

  if (estimatedResults?.nonAid) {
    rows.push(
      buildRow(
        NON_AID_KEY,
        estimatedResults.nonAid.key || NON_AID_RESULT_KEY,
        NON_AID_POPULATION_LABEL,
        estimatedResults.nonAid,
        actualResults?.nonAid,
        showLastYear,
        estimatedResults.nonAidRepackage
      )
    );
  }

  (estimatedResults?.excludedGroups ?? []).forEach((group, index) => {
    const label =
      !group.key || group.key === UNKNOWN_KEY ? UNKNOWN_LABEL : group.key;
    const resultKey = group.key || '';

    rows.push(
      buildRow(
        group.key || `excluded-${index}`,
        resultKey,
        label,
        group,
        findByKey(actualResults?.excludedGroups, group.key),
        showLastYear,
        resultKey
          ? estimatedResults?.excludedGroupRepackageStatuses?.[resultKey]
          : undefined
      )
    );
  });

  return rows;
};
